import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  Grid,
  Tabs,
  Tab,
  Card,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Avatar,
  IconButton,
} from "@mui/material";
import {
  Store,
  ExternalLink,
  Package,
  ShieldCheck,
  X,
  LogIn,
} from "lucide-react";
import Axios from "../../../Common/api/axiosConfig";
import { uploadLink } from "../../../utils/constants";

interface TenantDetailsModalProps {
  open: boolean;
  onClose: () => void;
  tenantId: string | null;
  onImpersonate: (tenantId: string, email: string) => void;
}

export const TenantDetailsModal: React.FC<TenantDetailsModalProps> = ({
  open,
  onClose,
  tenantId,
  onImpersonate,
}) => {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && tenantId) {
      fetchTenantDetails();
    } else {
      setData(null);
      setTabValue(0);
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tenantId]);

  const fetchTenantDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await Axios.get(`/admin/tenants/${tenantId}`);
      setData(res.data.data);
    } catch (err: any) {
      console.error("Error fetching tenant details:", err);
      setError(
        err?.response?.data?.message || "Failed to load tenant details"
      );
    } finally {
      setLoading(false);
    }
  };

  const store = data?.store;
  const stores = data?.stores || (data?.store ? [data.store] : []);
  const user = data?.user;
  const products = data?.products || [];
  const dashboard = data?.dashboard;
  const warrantyRegistrations = data?.warrantyRegistrations || [];

  const storeUrl = store?.url
    ? store.url.includes(".")
      ? `https://${store.url}`
      : `/${store.url}`
    : null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1,
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Store className="text-primary" size={24} />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              {store?.name || user?.email || "Tenant Details"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              ID: {user?._id || tenantId}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small">
          <X size={20} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box sx={{ textAlign: "center", py: 6, color: "error.main" }}>
            <Typography variant="body1">{error}</Typography>
          </Box>
        ) : data ? (
          <Box>
            {/* Top Quick Badges */}
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1.5,
                mb: 2.5,
                alignItems: "center",
              }}
            >
              <Chip
                label={user?.role === "superadmin" ? "Superadmin" : "Tenant"}
                color={user?.role === "superadmin" ? "secondary" : "default"}
                size="small"
                sx={{ fontWeight: 600 }}
              />
              <Chip
                label={user?.status === "suspended" ? "Suspended" : "Active"}
                color={user?.status === "suspended" ? "error" : "success"}
                size="small"
                sx={{ fontWeight: 600 }}
              />
              {store?.visibility !== undefined && (
                <Chip
                  label={store.visibility ? "Store Online" : "Store Offline"}
                  color={store.visibility ? "primary" : "default"}
                  variant="outlined"
                  size="small"
                />
              )}
              {storeUrl && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<ExternalLink size={14} />}
                  onClick={() => window.open(storeUrl, "_blank")}
                  sx={{ textTransform: "none", ml: "auto" }}
                >
                  Visit Storefront
                </Button>
              )}
            </Box>

            {/* Metric Summary Cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6} sm={3}>
                <Card
                  variant="outlined"
                  sx={{ p: 1.5, textAlign: "center", borderRadius: 2 }}
                >
                  <Typography variant="caption" color="text.secondary">
                    Products
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {products.length}
                  </Typography>
                </Card>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Card
                  variant="outlined"
                  sx={{ p: 1.5, textAlign: "center", borderRadius: 2 }}
                >
                  <Typography variant="caption" color="text.secondary">
                    Total Orders
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {dashboard?.orders || 0}
                  </Typography>
                </Card>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Card
                  variant="outlined"
                  sx={{ p: 1.5, textAlign: "center", borderRadius: 2 }}
                >
                  <Typography variant="caption" color="text.secondary">
                    Store Revenue
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 700, color: "success.main" }}
                  >
                    ₹{(dashboard?.revenue || 0).toLocaleString()}
                  </Typography>
                </Card>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Card
                  variant="outlined"
                  sx={{ p: 1.5, textAlign: "center", borderRadius: 2 }}
                >
                  <Typography variant="caption" color="text.secondary">
                    Warranties
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {warrantyRegistrations.length}
                  </Typography>
                </Card>
              </Grid>
            </Grid>

            {/* Navigation Tabs */}
            <Tabs
              value={tabValue}
              onChange={(_, val) => setTabValue(val)}
              sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}
            >
              <Tab
                label="Store Overview"
                icon={<Store size={16} />}
                iconPosition="start"
                sx={{ textTransform: "none", fontWeight: 600 }}
              />
              <Tab
                label={`Products (${products.length})`}
                icon={<Package size={16} />}
                iconPosition="start"
                sx={{ textTransform: "none", fontWeight: 600 }}
              />
              <Tab
                label={`Warranties (${warrantyRegistrations.length})`}
                icon={<ShieldCheck size={16} />}
                iconPosition="start"
                sx={{ textTransform: "none", fontWeight: 600 }}
              />
            </Tabs>

            {/* TAB 0: Store Overview */}
            {tabValue === 0 && (
              <Box sx={{ pt: 1 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Owner Email
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, mb: 2 }}
                    >
                      {user?.email || "N/A"}
                    </Typography>

                    <Typography variant="subtitle2" color="text.secondary">
                      Store Name
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, mb: 2 }}
                    >
                      {store?.name || "Not Configured"}
                    </Typography>

                    <Typography variant="subtitle2" color="text.secondary">
                      Contact Phone / WhatsApp
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, mb: 2 }}
                    >
                      {store?.number ? `+${store.number}` : "N/A"}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Store URL / Slug
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, mb: 2 }}
                    >
                      {store?.url || "N/A"}
                    </Typography>

                    <Typography variant="subtitle2" color="text.secondary">
                      Operating Hours
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, mb: 2 }}
                    >
                      {store?.operating_hours || "N/A"}
                    </Typography>

                    <Typography variant="subtitle2" color="text.secondary">
                      Address
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, mb: 2 }}
                    >
                      {store?.address || "N/A"}
                    </Typography>
                  </Grid>

                  {store?.description && (
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Store Description
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          p: 1.5,
                          backgroundColor: "action.hover",
                          borderRadius: 1,
                        }}
                      >
                        {store.description}
                      </Typography>
                    </Grid>
                  )}

                  {store?.header?.categories &&
                    store.header.categories.length > 0 && (
                      <Grid item xs={12}>
                        <Typography
                          variant="subtitle2"
                          color="text.secondary"
                          sx={{ mb: 1 }}
                        >
                          Configured Categories
                        </Typography>
                        <Box
                          sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}
                        >
                          {store.header.categories.map((c: any, i: number) => (
                            <Chip
                              key={i}
                              size="small"
                              label={c.name || c}
                              variant="outlined"
                            />
                          ))}
                        </Box>
                      </Grid>
                    )}
                  {stores && stores.length > 1 && (
                    <Grid item xs={12} sx={{ mt: 1 }}>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        sx={{ fontWeight: 700, mb: 1 }}
                      >
                        All Stores Owned by this Tenant ({stores.length})
                      </Typography>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Store Name</TableCell>
                            <TableCell>Store URL</TableCell>
                            <TableCell>WhatsApp Phone</TableCell>
                            <TableCell>Status</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {stores.map((s: any) => (
                            <TableRow key={s._id}>
                              <TableCell sx={{ fontWeight: 600 }}>{s.name}</TableCell>
                              <TableCell>
                                <Typography
                                  variant="caption"
                                  color="primary"
                                  component="a"
                                  href={s.url?.includes(".") ? `https://${s.url}` : `/${s.url}`}
                                  target="_blank"
                                  rel="noreferrer"
                                  sx={{ textDecoration: "none", fontWeight: 600 }}
                                >
                                  {s.url}
                                </Typography>
                              </TableCell>
                              <TableCell>{s.number ? `+${s.number}` : "None"}</TableCell>
                              <TableCell>
                                <Chip
                                  label={s.visibility ? "Online" : "Offline"}
                                  size="small"
                                  color={s.visibility ? "success" : "default"}
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Grid>
                  )}
                </Grid>
              </Box>
            )}

            {/* TAB 1: Products */}
            {tabValue === 1 && (
              <Box sx={{ pt: 1 }}>
                {products.length === 0 ? (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ textAlign: "center", py: 4 }}
                  >
                    No products added by this tenant yet.
                  </Typography>
                ) : (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Product</TableCell>
                        <TableCell>Category</TableCell>
                        <TableCell align="right">Price</TableCell>
                        <TableCell align="center">Stock</TableCell>
                        <TableCell align="center">Warranty</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {products.map((p: any) => {
                        const img =
                          p.images && p.images[0]
                            ? `${uploadLink}${p.images[0]}`
                            : null;
                        return (
                          <TableRow key={p._id}>
                            <TableCell sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                              <Avatar
                                src={img || undefined}
                                variant="rounded"
                                sx={{ width: 36, height: 36 }}
                              >
                                <Package size={18} />
                              </Avatar>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {p.name}
                              </Typography>
                            </TableCell>
                            <TableCell>{p.category || "General"}</TableCell>
                            <TableCell align="right">₹{p.price}</TableCell>
                            <TableCell align="center">
                              <Chip
                                label={p.inStock ? "In Stock" : "Out of Stock"}
                                size="small"
                                color={p.inStock ? "success" : "default"}
                              />
                            </TableCell>
                            <TableCell align="center">
                              {p.hasWarranty ? (
                                <Chip
                                  label={p.warrantyPeriod || "Enabled"}
                                  size="small"
                                  color="info"
                                  variant="outlined"
                                />
                              ) : (
                                <Typography variant="caption" color="text.secondary">
                                  None
                                </Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </Box>
            )}

            {/* TAB 2: Warranties */}
            {tabValue === 2 && (
              <Box sx={{ pt: 1 }}>
                {warrantyRegistrations.length === 0 ? (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ textAlign: "center", py: 4 }}
                  >
                    No warranty registrations submitted for this store.
                  </Typography>
                ) : (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Customer</TableCell>
                        <TableCell>Product</TableCell>
                        <TableCell>Order ID</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {warrantyRegistrations.map((w: any) => (
                        <TableRow key={w._id}>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {w.customerName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {w.email}
                            </Typography>
                          </TableCell>
                          <TableCell>{w.productName}</TableCell>
                          <TableCell>{w.orderId}</TableCell>
                          <TableCell>
                            <Chip
                              label={w.status}
                              size="small"
                              color={
                                w.status === "verified"
                                  ? "success"
                                  : w.status === "rejected"
                                  ? "error"
                                  : "warning"
                              }
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </Box>
            )}
          </Box>
        ) : null}
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: "1px solid", borderColor: "divider" }}>
        {user && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<LogIn size={16} />}
            onClick={() => onImpersonate(user._id, user.email)}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Login As Tenant
          </Button>
        )}
        <Button onClick={onClose} variant="outlined" sx={{ textTransform: "none" }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TenantDetailsModal;
